"use client";
import { GalleryData } from "@/lib/types";
import Image from "next/image";
import { motion } from "framer-motion";

interface InfiniteMarqueeProps {
  items?: GalleryData[];
  direction?: "left" | "right" | "up" | "down";
  speed?: number;
  className?: string;
  imageClassName?: string;
  innerClassName?: string;
  isImageFill?: boolean;
  gap?: string;
  /**
   * Width the tile really occupies, as a `sizes` list. Without it a logo strip
   * 48 px tall still downloaded the 768 px candidate, because Next falls back
   * to 100vw. Only the picked srcset entry changes — the rendered size does not.
   */
  sizes?: string;
}
const InfiniteMarquee = ({
  items = [],
  direction = "left",
  speed = 100,
  className = "",
  imageClassName = "",
  innerClassName = "object-cover",
  isImageFill = true,
  gap = "gap-4 lg:gap-6",
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
}: InfiniteMarqueeProps) => {
  const isVertical = direction === "up" || direction === "down";
  const yAnimation = direction === "up" ? ["0%", "-50%"] : ["-50%", "0%"];
  const xAnimation = direction === "left" ? ["0%", "-50%"] : ["-50%", "0%"];
  const duplicatedItems = [...items, ...items, ...items, ...items];
  return (
    <div className={`overflow-hidden relative ${className}`}>
      <motion.div
        animate={{
          y: isVertical ? yAnimation : 0,
          x: !isVertical ? xAnimation : 0,
        }}
        transition={{
          repeat: Infinity,
          ease: "linear",
          duration: speed,
        }}
        style={{ willChange: "transform" }}
        className={`flex transform-gpu ${
          isVertical
            ? `flex-col w-full h-max ${gap}`
            : `flex-row h-full w-max ${gap} items-center`
        } `}
      >
        {duplicatedItems.map((img, index) => (
          <div
            key={index}
            className={`relative shrink-0 overflow-hidden ${imageClassName}`}
          >
            {isImageFill ? (
              <Image
                src={img?.src || ""}
                alt={img?.alt || `Octarnal client brand ${index + 1}`}
                fill
                className={innerClassName}
                sizes={sizes}
              />
            ) : (
              <Image
                src={img?.src || ""}
                alt={img?.alt || `Octarnal client brand ${index + 1}`}
                width={700}
                height={250}
                sizes={sizes}
                className={innerClassName}
              />
            )}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default InfiniteMarquee;
